/* Shop4Seats — seat configurator (product page, ES module, no framework).
 * Reads the compact JSON from snippets/pdp-config-json and drives every buy-column block by data attributes:
 * colour (tiles, chips, library drawer) → seats → upgrades → plan → seat base → add-ons → delivery → summary.
 * The server renders the initial state, so nothing shifts on load; this file only toggles attributes and text.
 * Variant change: document 's4s:variant-change' { variantId } (gallery listens) + ?variant= in the URL.
 * Add to Cart: one line for the selected Seats variant ("Both seats" = one item); upgrades, bases, plan, add-ons and
 * white-glove delivery are nested under it (parent_id), so the cart shows the whole configuration as one product
 * with its children (snippets/cart-line-children). */

const root = document.querySelector('s4s-product');
const cfgEl = root && root.querySelector('[data-config]');


/* ---------- helpers ---------- */
// After a successful add the button reads "Added ✓" for a moment, then is ready to add again
function flashAdded(st, paint) {
  st.added = true;
  clearTimeout(st.addedTimer);
  st.addedTimer = setTimeout(() => { st.added = false; paint(); }, 2500);
}
function money(cents) {
  if (window.S4S && S4S.money) return S4S.money(cents);
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const $$ = (sel, el) => Array.from(el.querySelectorAll(sel));
const setText = (els, txt) => els.forEach((el) => { if (el.textContent !== txt) el.textContent = txt; });
const check = (el, on) => el.setAttribute('aria-checked', on ? 'true' : 'false');
const SIDE_LABEL = { driver: 'Driver seat', passenger: 'Passenger seat' };
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);

function initConfigurator(root, C) {
  const cp = C.colourPos;
  const sp = C.seatsPos;
  const seatKeys = Object.keys(C.seats);
  const upgById = Object.fromEntries(C.upgrades.map((u) => [String(u.id), u]));
  const baseById = Object.fromEntries(C.bases.map((b) => [String(b.id), b]));
  const variant = (colour, seat) => C.variants.find((v) => v.o[cp] === colour && v.o[sp] === seat);
  const singleSeatFor = (side) => seatKeys.find((k) => C.seats[k].count === 1 && C.seats[k].sides.length === 1 && C.seats[k].sides[0] === side);
  const singleSeatPrice = (colour) => {
    const k = seatKeys.find((s) => C.seats[s].count === 1);
    const v = k && variant(colour, k);
    return v ? v.p : C.basePrice;
  };

  const start = C.variants.find((v) => String(v.id) === root.dataset.variantId) || C.variants[0];
  const st = {
    colour: start.o[cp], seat: start.o[sp],
    sel: {}, open: {}, details: {}, flash: {},
    plan: 'none', baseMode: 'find', wiz: { type: null, position: null, chassis: null, power: null, year: '' }, bases: {},
    addons: {}, delivery: 'standard', rows: 1, added: false, busy: false,
  };
  const timers = {};

  const sides = () => C.seats[st.seat].sides;
  const seatCount = () => C.seats[st.seat].count;
  const isBoth = () => sides().length === 2;
  const colourInfo = () => {
    const c = C.colours[st.colour] || { name: st.colour, code: '', material: '', grade: '', stocked: true, ship: '' };
    const delta = singleSeatPrice(st.colour) - C.basePrice;
    const gradeLabel = c.stocked || !c.grade ? c.material : c.material + ' · Grade ' + c.grade;
    return Object.assign({}, c, { delta, gradeLabel });
  };
  const deltaText = (d) => (d === 0 ? 'No upcharge' : (d > 0 ? '+' : '−') + money(Math.abs(d)) + ' per seat');
  const leadText = (c) => (c.stocked ? 'In stock · ' + (c.ship || 'Ships in 1 business day') : 'Made to order · ' + c.ship);
  const tierOf = (u, sel) => sel && u.tiers.find((t) => t.id === sel.tier);

  /* Seat lines that go to the cart: one per side (driver / passenger variants), else the selected variant */
  // One cart line per purchase: the selected Seats variant ("Both seats" is one item priced for two seats)
  function seatLines() {
    return [{ side: null, v: variant(st.colour, st.seat) }];
  }

  /* ---------- Base wizard ---------- */
  function activeBaseSet() {
    const w = st.wiz;
    if (!w.type || !w.position) return null;
    if (w.position === 'midrear') return 'midrear';
    if (w.type === 'A') return w.power ? (w.power === 'power' ? 'a_power' : 'a_manual') : null;
    if (!w.chassis || !w.power) return null;
    return w.chassis === 'ford' ? 'c_ford' : 'c_chevy';
  }
  const visibleBases = () => {
    const key = activeBaseSet();
    if (!key) return [];
    return C.bases.filter((b) => b.set === key && (!b.side || sides().includes(b.side)));
  };
  const chosenBases = () => Object.keys(st.bases).filter((id) => st.bases[id] && baseById[id]).map((id) => baseById[id]);

  /* ---------- Totals ---------- */
  function upgradeTotal() {
    let sum = 0;
    for (const id in st.sel) {
      const t = tierOf(upgById[id], st.sel[id]);
      if (t) sum += t.price * st.sel[id].sides.length;
    }
    return sum;
  }
  const planObj = () => C.plans.find((p) => String(p.id) === String(st.plan));
  function total() {
    let t = seatLines().reduce((s, l) => s + (l.v ? l.v.p : 0), 0) + upgradeTotal();
    const p = planObj();
    if (p) t += p.price * seatCount();
    chosenBases().forEach((b) => { t += b.price; });
    C.addons.forEach((a) => { t += a.price * (st.addons[a.id] || 0); });
    if (st.delivery === 'white' && C.whiteGlove) t += C.whiteGlove.price * st.rows;
    return t;
  }
  const upgradeCount = () => Object.keys(st.sel).filter((k) => st.sel[k].sides.length > 0).length;
  const subline = () => {
    const n = chosenBases().length;
    return plural(seatCount(), 'seat', 'seats') + ' · ' + plural(upgradeCount(), 'upgrade', 'upgrades') + ' · ' + plural(n, 'base item', 'base items');
  };
  const fullTitle = () => {
    const c = colourInfo();
    const phrase = isBoth() ? 'Driver and Passenger' : (C.seats[st.seat].label || st.seat).replace(/\s*seat\s*$/i, '');
    return C.title + ' / ' + c.name + ' / ' + phrase;
  };

  /* ---------- Actions ---------- */
  function set(patch) {
    Object.assign(st, patch);
    st.added = false;
    render();
  }

  function setColour(value) {
    if (!value || value === st.colour) return;
    set({ colour: value });
    variantChanged();
  }

  function setSeat(value) {
    if (!C.seats[value] || value === st.seat) return;
    const allowed = C.seats[value].sides;
    const sel = {};
    for (const k in st.sel) {
      const s = st.sel[k].sides.filter((sd) => allowed.includes(sd));
      if (s.length) sel[k] = { tier: st.sel[k].tier, sides: s };
    }
    const bases = {};
    for (const id in st.bases) {
      const b = baseById[id];
      if (b && (!b.side || allowed.includes(b.side))) bases[id] = true;
    }
    set({ seat: value, sel, bases, open: {} });
    variantChanged();
  }

  function variantChanged() {
    const v = variant(st.colour, st.seat);
    if (!v) return;
    root.dataset.variantId = v.id;
    const url = new URL(location.href);
    url.searchParams.set('variant', v.id);
    history.replaceState(history.state, '', url);
    document.dispatchEvent(new CustomEvent('s4s:variant-change', { detail: { variantId: v.id, variant: v } }));
  }

  /* ---------- Rendering ---------- */
  function render() {
    renderColour();
    renderSeats();
    renderUpgrades();
    renderPlans();
    renderBase();
    renderAddons();
    renderDelivery();
    renderSummary();
  }

  function renderColour() {
    $$('[data-colour]', root).forEach((el) => check(el, el.dataset.colour === st.colour));
    const lib = document.getElementById('ColourLibrary');
    if (!lib) return;
    const c = colourInfo();
    setText($$('[data-sel-name]', lib), c.name);
    setText($$('[data-sel-meta]', lib), [c.code, c.gradeLabel, deltaText(c.delta)].filter(Boolean).join(' · '));
  }

  function renderSeats() {
    $$('[data-seat]', root).forEach((el) => {
      check(el, el.dataset.seat === st.seat);
      const v = variant(st.colour, el.dataset.seat);
      const p = el.querySelector('[data-seat-price]');
      if (p && v) p.textContent = money(v.p);
    });
  }

  function renderUpgrades() {
    const box = root.querySelector('[data-cfg="upgrades"]');
    if (!box) return;
    const active = sides();
    const both = active.length === 2;
    const intro = box.querySelector('[data-upg-intro]');
    if (intro) {
      intro.textContent = C.seats[st.seat].intro || (both
        ? 'Prices shown per seat. Click Add, then tick the seat or seats you want it fitted to.'
        : 'Every seat is complete as standard. Click Add to fit an upgrade to your ' + active[0] + ' seat.');
    }
    $$('.upg[data-upg]', box).forEach((card) => {
      const id = card.dataset.upg;
      const u = upgById[id];
      if (!u) return;
      const allowed = active.filter((sd) => u[sd]);
      card.hidden = allowed.length === 0;
      if (card.hidden) return;
      const sel = st.sel[id];
      const tier = tierOf(u, sel);
      const scope = sel ? sel.sides : [];
      const isOpen = !!st.open[id];
      const multi = u.tiers.length > 1;
      const minPrice = Math.min(...u.tiers.map((t) => t.price));
      const fitted = !!tier && scope.length > 0;
      const flashing = !!st.flash[id] && !isOpen;
      const detailsOpen = !!st.details[id];

      let label = u.short || '';
      if (tier) {
        label = tier.label;
        if (both) label += scope.length === 0 ? ' — tick a seat below' : scope.length === 2 ? ' — both seats' : scope[0] === 'driver' ? ' — driver only' : ' — passenger only';
      }
      const perSeat = (multi ? 'From +' : '+') + money(minPrice);
      const priceLine = fitted
        ? '+' + money(tier.price * scope.length) + (scope.length === 2 ? ' · 2 seats' : '')
        : perSeat + (both ? ' per seat' : '');

      card.classList.toggle('is-fitted', fitted);
      setText($$('[data-upg-label]', card), label);
      setText($$('[data-upg-price]', card), priceLine);
      const q = (s) => card.querySelector(s);
      q('[data-upg-add]').hidden = !!tier || isOpen || flashing;
      q('[data-upg-flash]').hidden = !flashing;
      q('[data-upg-edit]').hidden = !(fitted && !isOpen && !flashing);
      const done = q('[data-upg-done]');
      done.hidden = !isOpen;
      done.textContent = both && scope.length === 0 ? 'Cancel' : 'Done';
      q('[data-upg-remove]').hidden = !isOpen;
      const det = q('[data-upg-details]');
      det.hidden = isOpen;
      det.setAttribute('aria-expanded', detailsOpen ? 'true' : 'false');
      const bullets = q('[data-upg-bullets]');
      if (bullets) bullets.hidden = !(detailsOpen || isOpen);
      q('[data-upg-panel]').hidden = !(isOpen && (multi || both));
      const sidesBox = q('[data-upg-sides]');
      if (sidesBox) sidesBox.hidden = !both;

      $$('[data-upg-tier]', card).forEach((el) => {
        const t = u.tiers.find((x) => String(x.id) === el.dataset.upgTier);
        check(el, !!tier && String(tier.id) === el.dataset.upgTier);
        const pr = el.querySelector('[data-tier-price]');
        if (pr && t) pr.textContent = '+' + money(t.price) + (both ? ' ea.' : '');
      });
      $$('[data-upg-side]', card).forEach((el) => {
        const sd = el.dataset.upgSide;
        check(el, scope.includes(sd));
        const pr = el.querySelector('[data-side-price]');
        if (pr && u[sd]) pr.textContent = '+' + money(tier ? tier.price : minPrice);
      });
    });
  }

  function renderPlans() {
    const n = seatCount();
    $$('[data-plan]', root).forEach((el) => {
      check(el, String(el.dataset.plan) === String(st.plan));
      const pr = el.querySelector('[data-plan-price]');
      if (pr) pr.textContent = money(Number(pr.dataset.planPrice) * n) + ' for ' + plural(n, 'seat', 'seats');
    });
  }

  function choiceStep(label, field, opts) {
    return '<div class="base__step"><div class="label-caps base__step-label">' + esc(label) + '</div>'
      + '<div class="base__choices" role="radiogroup" aria-label="' + esc(label) + '">'
      + opts.map((o) => '<button type="button" class="pill-choice" role="radio" aria-checked="' + (st.wiz[field] === o.v) + '" data-wiz="' + field + '" data-value="' + o.v + '">'
        + '<span class="pill-choice__title">' + esc(o.t) + '</span><span class="pill-choice__sub">' + esc(o.sub) + '</span></button>').join('')
      + '</div></div>';
  }

  function renderBase() {
    const box = root.querySelector('[data-cfg="base"]');
    if (!box) return;
    $$('[data-base-mode]', box).forEach((el) => check(el, el.dataset.baseMode === st.baseMode));
    const finder = box.querySelector('[data-base-finder]');
    if (finder) finder.hidden = st.baseMode !== 'find';
    const stepsEl = box.querySelector('[data-base-steps]');
    const w = st.wiz;
    let html = choiceStep('Motorhome type', 'type', [
      { v: 'A', t: 'Class A', sub: 'Flat-front coach' },
      { v: 'C', t: 'Class C', sub: 'Van cab, over-cab bunk' },
    ]);
    if (w.type) {
      html += choiceStep('Seat position', 'position', [
        { v: 'front', t: 'Front', sub: 'Driver / passenger' },
        { v: 'midrear', t: 'Middle or rear', sub: 'Lounge position' },
      ]);
    }
    const cFront = w.type === 'C' && w.position === 'front';
    if (cFront) {
      html += choiceStep('Chassis', 'chassis', [
        { v: 'chevy', t: 'Chevy / GMC', sub: 'Full-size van' },
        { v: 'ford', t: 'Ford', sub: 'E-Series van' },
      ]);
    }
    if (w.position === 'front' && (w.type === 'A' || w.chassis)) {
      html += choiceStep('Base type', 'power', [
        { v: 'manual', t: 'Manual', sub: 'Swivel and slide by hand' },
        { v: 'power', t: 'Power', sub: 'Motorised adjustment' },
      ]);
    }
    if (cFront && w.power) {
      const from = Number(box.dataset.yearFrom) || 1969;
      const to = Number(box.dataset.yearTo) || 2013;
      let opts = '<option value="">— Select coach year —</option>';
      for (let y = from; y <= to; y++) opts += '<option value="' + y + '"' + (String(y) === w.year ? ' selected' : '') + '>' + y + '</option>';
      html += '<div class="base__step"><div class="label-caps base__step-label">Coach year</div>'
        + '<select class="field-select base__select" data-wiz-year aria-label="Coach year">' + opts + '</select></div>';
    }
    const vis = visibleBases();
    if (vis.length && (!cFront || w.year)) {
      html += '<div class="base__step"><div class="label-caps base__step-label">Recommended for your coach</div><div class="base__picks" role="group" aria-label="Recommended for your coach">'
        + vis.map((b) => '<button type="button" class="base-pick" role="checkbox" aria-checked="' + !!st.bases[b.id] + '" data-base-pick="' + b.id + '">'
          + '<span class="check-box"></span>'
          + '<span class="base-pick__img">' + (b.img ? '<img src="' + esc(b.img) + '" alt="" width="42" height="42" loading="lazy">' : '') + '</span>'
          + '<span class="base-pick__body"><span class="base-pick__title">' + esc(b.t) + '</span><span class="base-pick__sub">' + esc(b.sub) + '</span></span>'
          + '<span class="base-pick__price">' + money(b.price) + '</span></button>').join('')
        + '</div></div>';
    }
    if (stepsEl.dataset.html !== html) {
      const focusKey = document.activeElement && stepsEl.contains(document.activeElement)
        ? (document.activeElement.dataset.basePick ? '[data-base-pick="' + document.activeElement.dataset.basePick + '"]'
          : document.activeElement.dataset.wiz ? '[data-wiz="' + document.activeElement.dataset.wiz + '"][data-value="' + document.activeElement.dataset.value + '"]'
            : document.activeElement.matches('[data-wiz-year]') ? '[data-wiz-year]' : null)
        : null;
      stepsEl.innerHTML = html;
      stepsEl.dataset.html = html;
      if (focusKey) stepsEl.querySelector(focusKey)?.focus();
    }
    const chosen = chosenBases();
    const chosenEl = box.querySelector('[data-base-chosen]');
    chosenEl.hidden = chosen.length === 0;
    if (chosen.length) {
      const ctx = (w.type === 'A' ? 'Class A' : w.type === 'C' ? 'Class C' : '')
        + (w.chassis ? ' · ' + (w.chassis === 'ford' ? 'Ford' : 'Chevy/GMC') : '') + (w.power ? ' · ' + w.power : '') + (w.year ? ' · ' + w.year : '');
      chosenEl.innerHTML = '<div class="base__chosen-head">' + esc(ctx || 'Selected bases') + '</div>'
        + chosen.map((b) => '<div class="base__chosen-line"><span>' + esc(b.t + (b.sub ? ' (' + b.sub + ')' : '')) + '</span><span>' + money(b.price) + '</span></div>').join('');
    }
  }

  function renderAddons() {
    $$('[data-addon]', root).forEach((el) => {
      const id = el.dataset.addon;
      const a = C.addons.find((x) => String(x.id) === id);
      if (!a) return;
      const n = st.addons[id] || 0;
      el.classList.toggle('is-on', n > 0);
      setText($$('[data-addon-qty]', el), String(n));
      el.querySelector('[data-addon-dec]').disabled = n === 0;
      setText($$('[data-addon-price]', el), money(a.price) + ' each' + (n ? ' · ' + money(a.price * n) : ''));
    });
  }

  function renderDelivery() {
    const box = root.querySelector('[data-cfg="delivery"]');
    if (!box) return;
    $$('[data-delivery]', box).forEach((el) => check(el, el.dataset.delivery === st.delivery));
    const rows = box.querySelector('[data-delivery-rows]');
    rows.hidden = st.delivery !== 'white';
    if (C.whiteGlove) {
      setText($$('[data-rows-val]', box), String(st.rows));
      setText($$('[data-rows-line]', box), plural(st.rows, 'row', 'rows') + ' × ' + money(C.whiteGlove.price) + ' = ' + money(C.whiteGlove.price * st.rows));
    }
    const max = Number(box.dataset.maxRows) || 4;
    box.querySelector('[data-rows-dec]').disabled = st.rows <= 1;
    box.querySelector('[data-rows-inc]').disabled = st.rows >= max;
  }

  function summaryGroups() {
    const c = colourInfo();
    const chosen = chosenBases();
    const line = (label, price, cls) => '<div class="summary__line' + (cls ? ' summary__line--' + cls : '') + '"><span>' + esc(label) + '</span><span>' + price + '</span></div>';
    const group = (title, lines, sub) => '<div class="summary__group"><div class="label-caps summary__group-title">' + esc(title) + '</div>' + lines
      + '<div class="summary__subtotal"><span>' + esc(title) + ' subtotal</span><span class="t-num">' + money(sub) + '</span></div></div>';
    let html = '';
    for (const sd of sides()) {
      let sub = C.basePrice;
      let lines = line(C.shortTitle, money(C.basePrice));
      let n = 0;
      if (c.delta) { lines += line('+ ' + c.name + ' (made to order)', (c.delta > 0 ? '+' : '−') + money(Math.abs(c.delta)), 'sub'); sub += c.delta; }
      C.upgrades.forEach((u) => {
        const s = st.sel[u.id];
        if (!s || !s.sides.includes(sd)) return;
        const t = tierOf(u, s);
        if (!t) return;
        lines += line('+ ' + t.label, '+' + money(t.price), 'sub'); sub += t.price; n++;
      });
      chosen.forEach((b) => { if (b.side === sd) { lines += line('+ ' + b.t, '+' + money(b.price), 'sub'); sub += b.price; n++; } });
      if (!n && !c.delta) lines += line('No upgrades added', '—', 'muted');
      html += group(SIDE_LABEL[sd] || 'Seat', lines, sub);
    }
    const shared = chosen.filter((b) => !b.side);
    if (shared.length) {
      html += group('Shared hardware', shared.map((b) => line('+ ' + b.t, '+' + money(b.price), 'sub')).join(''), shared.reduce((s, b) => s + b.price, 0));
    }
    const p = planObj();
    if (p) {
      const n = seatCount();
      html += group('Protection plan', line(p.label + ' × ' + plural(n, 'seat', 'seats'), '+' + money(p.price * n), 'sub'), p.price * n);
    }
    let addSub = 0;
    const addLines = C.addons.map((a) => {
      const q = st.addons[a.id] || 0;
      if (!q) return '';
      addSub += a.price * q;
      return line(a.name + ' × ' + q, '+' + money(a.price * q), 'sub');
    }).join('');
    if (addLines) html += group('Add-ons', addLines, addSub);
    if (st.delivery === 'white' && C.whiteGlove) {
      const d = C.whiteGlove.price * st.rows;
      html += group('Delivery', line('White-glove delivery × ' + plural(st.rows, 'row', 'rows'), '+' + money(d), 'sub'), d);
    }
    return html;
  }

  function renderSummary() {
    const c = colourInfo();
    const t = total();
    const v = variant(st.colour, st.seat);
    setText($$('[data-sum-subline]', root), subline());
    setText($$('[data-sum-title]', root), fullTitle());
    setText($$('[data-sum-meta]', root), [c.code, c.gradeLabel, deltaText(c.delta)].filter(Boolean).join(' · '));
    const vl = (variant(st.colour, st.seat) || {}).l; // variant override (s4s.lead_time)
    $$('[data-sum-lead]', root).forEach((el) => { el.textContent = vl || leadText(c); el.classList.toggle('is-stock', !vl && !!c.stocked); });
    setText($$('[data-sum-total]', root), money(t));
    $$('[data-sum-finance]', root).forEach((el) => {
      const m = Number(el.dataset.months) || C.financing || 12;
      el.textContent = 'or ' + money(Math.round(t / m)) + '/mo for ' + m + ' months with Affirm · 0% APR';
    });
    const groups = root.querySelector('[data-sum-groups]');
    if (groups) {
      const html = summaryGroups();
      if (groups.dataset.html !== html) { groups.innerHTML = html; groups.dataset.html = html; }
    }
    const thumb = root.querySelector('.summary__thumb img');
    if (thumb && v && v.img && thumb.dataset.src !== v.img) {
      thumb.removeAttribute('srcset');
      thumb.src = v.img;
      thumb.dataset.src = v.img;
    }
    const available = !!(v && v.a);
    $$('[data-add-to-cart]', root).forEach((b) => {
      b.textContent = !available ? 'Sold out' : st.added ? 'Added to cart ✓' : 'Add to Cart';
      b.classList.toggle('is-loading', st.busy);
      b.disabled = !available || st.busy;
    });
  }

  /* ---------- Events ---------- */
  root.addEventListener('click', (e) => {
    const t = e.target.closest('button');
    if (!t || !root.contains(t)) return;
    const d = t.dataset;

    if (d.colour) return setColour(d.colour);
    if (d.seat) return setSeat(d.seat);

    const card = t.closest('.upg[data-upg]');
    if (card) {
      const id = card.dataset.upg;
      const u = upgById[id];
      const both = isBoth();
      if ('upgAdd' in d) {
        const allowed = sides().filter((sd) => u[sd]);
        const sel = Object.assign({}, st.sel, { [id]: { tier: u.tiers[0].id, sides: both ? [] : allowed } });
        const open = Object.assign({}, st.open, { [id]: both || u.tiers.length > 1 });
        const flash = Object.assign({}, st.flash);
        if (!both) {
          flash[id] = true;
          clearTimeout(timers[id]);
          timers[id] = setTimeout(() => { delete st.flash[id]; render(); }, Number(card.closest('[data-flash-ms]')?.dataset.flashMs) || 1000);
        }
        return set({ sel, open, flash });
      }
      if ('upgEdit' in d) return set({ open: Object.assign({}, st.open, { [id]: true }) });
      if ('upgDone' in d) {
        const sel = Object.assign({}, st.sel);
        if (sel[id] && !sel[id].sides.length) delete sel[id];
        return set({ open: Object.assign({}, st.open, { [id]: false }), sel });
      }
      if ('upgRemove' in d) {
        const sel = Object.assign({}, st.sel); delete sel[id];
        const open = Object.assign({}, st.open); delete open[id];
        const details = Object.assign({}, st.details); delete details[id];
        return set({ sel, open, details });
      }
      if ('upgDetails' in d) return set({ details: Object.assign({}, st.details, { [id]: !st.details[id] }) });
      if (d.upgTier) {
        const prev = st.sel[id];
        const seed = both ? [] : sides().filter((sd) => u[sd]);
        return set({ sel: Object.assign({}, st.sel, { [id]: { tier: Number(d.upgTier), sides: prev && prev.sides.length ? prev.sides : seed } }) });
      }
      if (d.upgSide) {
        const cur = st.sel[id] || { tier: u.tiers[0].id, sides: [] };
        const has = cur.sides.includes(d.upgSide);
        const next = has ? cur.sides.filter((x) => x !== d.upgSide) : cur.sides.concat(d.upgSide);
        return set({ sel: Object.assign({}, st.sel, { [id]: { tier: cur.tier, sides: ['driver', 'passenger'].filter((x) => next.includes(x)) } }) });
      }
      return;
    }

    if (d.plan) return set({ plan: d.plan });
    if (d.baseMode) return set({ baseMode: d.baseMode, bases: d.baseMode === 'have' ? {} : st.bases });
    if (d.wiz) {
      const w = Object.assign({}, st.wiz, { [d.wiz]: d.value });
      if (d.wiz === 'type') Object.assign(w, { position: null, chassis: null, power: null, year: '' });
      if (d.wiz === 'position') Object.assign(w, { chassis: null, power: null, year: '' });
      if (d.wiz === 'chassis') Object.assign(w, { power: null, year: '' });
      return set({ wiz: w, bases: {} });
    }
    if (d.basePick) return set({ bases: Object.assign({}, st.bases, { [d.basePick]: !st.bases[d.basePick] }) });

    const addon = t.closest('[data-addon]');
    if (addon && ('addonInc' in d || 'addonDec' in d)) {
      const id = addon.dataset.addon;
      const max = Number(addon.closest('[data-max]')?.dataset.max) || 9;
      const n = Math.max(0, Math.min(max, (st.addons[id] || 0) + ('addonInc' in d ? 1 : -1)));
      return set({ addons: Object.assign({}, st.addons, { [id]: n }) });
    }

    if (d.delivery) {
      const mobile = matchMedia('(max-width: 989px)').matches;
      const next = d.delivery === 'white' && st.delivery === 'white' && mobile ? 'standard' : d.delivery;
      return set({ delivery: next });
    }
    if ('rowsInc' in d || 'rowsDec' in d) {
      const max = Number(t.closest('[data-max-rows]')?.dataset.maxRows) || 4;
      return set({ rows: Math.max(1, Math.min(max, st.rows + ('rowsInc' in d ? 1 : -1))) });
    }

    if ('scrollSummary' in d) {
      const el = document.getElementById('summary');
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + scrollY - 80, behavior: 'smooth' });
      return;
    }
    if ('addToCart' in d) return addToCart();
  });

  root.addEventListener('change', (e) => {
    if (e.target.matches('[data-wiz-year]')) set({ wiz: Object.assign({}, st.wiz, { year: e.target.value }), bases: {} });
  });

  // Hover / focus on a colour → preload its gallery
  const preview = (e) => {
    const el = e.target.closest && e.target.closest('[data-colour]');
    if (!el) return;
    const v = variant(el.dataset.colour, st.seat);
    if (v) document.dispatchEvent(new CustomEvent('s4s:variant-preview', { detail: { variantId: v.id } }));
  };
  root.addEventListener('pointerover', preview, { passive: true });
  root.addEventListener('focusin', preview);

  /* ---------- Colour library: search + material / grade filters ---------- */
  const lib = document.getElementById('ColourLibrary');
  if (lib) {
    const items = $$('.library__item', lib);
    items.forEach((el) => {
      const d = singleSeatPrice(el.dataset.colour) - C.basePrice;
      const out = el.querySelector('[data-colour-delta]');
      if (out) out.textContent = deltaText(d);
    });
    const f = { q: '', mat: 'all', grade: 'all' };
    const apply = () => {
      let n = 0;
      items.forEach((el) => {
        const ok = (f.mat === 'all' || el.dataset.mat === f.mat) && (f.grade === 'all' || el.dataset.grade === f.grade) && (!f.q || el.dataset.q.includes(f.q));
        el.hidden = !ok;
        if (ok) n++;
      });
      setText($$('[data-library-count]', lib), String(n));
      const empty = lib.querySelector('[data-library-empty]');
      if (empty) empty.hidden = n > 0;
    };
    lib.querySelector('[data-library-q]')?.addEventListener('input', (e) => { f.q = e.target.value.trim().toLowerCase(); apply(); });
    lib.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-library-mat], [data-library-grade]');
      if (chip) {
        const key = chip.dataset.libraryMat ? 'mat' : 'grade';
        f[key] = chip.dataset.libraryMat || chip.dataset.libraryGrade;
        $$(key === 'mat' ? '[data-library-mat]' : '[data-library-grade]', lib).forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
        return apply();
      }
      const item = e.target.closest('.library__item');
      if (item) setColour(item.dataset.colour);
    });
  }

  /* ---------- Add to cart ---------- */
  async function addToCart() {
    if (st.busy) return;
    const err = root.querySelector('[data-cart-error]');
    if (err) err.hidden = true;
    const key = Date.now().toString(36);
    const items = [];
    const p = planObj();
    const chosen = chosenBases();
    // _group: how the cart shows the child ("fitted" = factory-fitted to that seat, "addon" = shipped with it);
    // _mode: "qty" lets the cart change its quantity, "fixed" shows ×N only
    // Same product under the same parent → one child line with the summed quantity (e.g. heater on both seats ×2)
    const child = (parent, id, quantity, group, mode) => {
      const hit = items.find((x) => x.parent_id === parent && x.id === id);
      if (hit) hit.quantity += quantity;
      else items.push({ id, quantity, parent_id: parent, properties: { _group: group, _mode: mode } });
    };
    const seat = variant(st.colour, st.seat);
    if (!seat) return;
    // 'Lead time' is a visible property, so checkout, the order and its emails show it (req 5.5)
    items.push({ id: seat.id, quantity: 1, properties: { _config: key, 'Lead time': seat.l || leadText(colourInfo()) } });
    C.upgrades.forEach((u) => {
      const s = st.sel[u.id];
      const t = tierOf(u, s);
      if (t) sides().forEach((sd) => { if (s.sides.includes(sd)) child(seat.id, t.id, 1, 'fitted', 'fixed'); });
    });
    chosen.forEach((b) => child(seat.id, b.id, 1, b.side ? 'fitted' : 'addon', 'fixed'));
    if (p) child(seat.id, p.id, seatCount(), 'fitted', 'fixed');
    C.addons.forEach((a) => { const q = st.addons[a.id] || 0; if (q) child(seat.id, a.id, q, 'addon', 'qty'); });
    if (st.delivery === 'white' && C.whiteGlove) child(seat.id, C.whiteGlove.id, st.rows, 'addon', 'qty');

    st.busy = true;
    renderSummary();
    try {
      try {
        await S4S.cart.add(items);
      } catch (e1) {
        // Store without nested cart lines: add the same items flat
        if (!items.some((i) => i.parent_id)) throw e1;
        await S4S.cart.add(items.map(({ parent_id, ...i }) => i)); // eslint-disable-line no-unused-vars
      }
      flashAdded(st, renderSummary);
      const sum = root.querySelector('[data-cfg="summary"]');
      if (!sum || sum.dataset.openDrawer !== 'false') S4S.cart.open();
    } catch (e2) {
      if (err) { err.textContent = e2.message || 'Could not add to cart. Please try again.'; err.hidden = false; }
    } finally {
      st.busy = false;
      renderSummary();
    }
  }

  /* ---------- Sticky bar ---------- */
  const bar = root.querySelector('[data-sticky-bar]');
  const cta = root.querySelector('.summary__cta');
  if (bar && cta) {
    const mq = matchMedia('(max-width: 989px)');
    let ctaAbove = false;
    const update = () => {
      let on;
      let ready = true;
      if (mq.matches) {
        const seats = document.getElementById('seats');
        const sum = document.getElementById('summary');
        on = !!seats && seats.getBoundingClientRect().top < 60;
        ready = !!sum && sum.getBoundingClientRect().top < innerHeight * 0.6;
        // Hide while the summary's own button is on screen
        const r = cta.getBoundingClientRect();
        if (r.top < innerHeight && r.bottom > 0) on = false;
      } else {
        on = ctaAbove;
      }
      bar.hidden = !on;
      bar.classList.toggle('is-ready', ready);
      document.body.classList.toggle('has-sticky-atc', on && mq.matches);
    };
    new IntersectionObserver(([e]) => { ctaAbove = !e.isIntersecting && e.boundingClientRect.top < 0; update(); }).observe(cta);
    let raf = 0;
    addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; if (mq.matches) update(); }); }, { passive: true });
    mq.addEventListener('change', update);
    update();
  }

  render();
}

/* ---------- Regular products (simple mode) ----------
 * Options: every checked input[data-option-index] in the product (variant-picker tiles/cards or buy-button pills).
 * Extras: input[data-extra] check rows (product-extras) — added at the same quantity, nested under the product.
 * total = (variant + extras) × qty → CTA label, [data-buy-total], [data-buy-monthly] (purchase-notes), sticky bar. */
function initBuyButtons(root) {
  const box = root.querySelector('s4s-buy-buttons');
  if (!box) return;
  const variants = JSON.parse(box.querySelector('[data-buy-variants]').textContent);
  const btn = box.querySelector('[data-buy-add]');
  const qtyEl = box.querySelector('[data-buy-qty]');
  const notes = root.querySelector('[data-months]');
  const months = notes ? Number(notes.dataset.months) : 12;
  const max = Number(box.dataset.max) || 12;
  const label = box.dataset.label || 'Add to cart — [total]';
  const bar = root.querySelector('[data-sticky-bar]');
  const st = { qty: 1, added: false, busy: false };

  const current = () => {
    const opts = [];
    $$('input[data-option-index]:checked', root).forEach((el) => { opts[Number(el.dataset.optionIndex)] = el.value; });
    return opts.length ? variants.find((v) => v.o.every((o, i) => o === opts[i])) : variants[0];
  };
  const extras = () => $$('input[data-extra]:checked', root);
  const total = (v) => (v.p + extras().reduce((n, el) => n + Number(el.dataset.price), 0)) * st.qty;

  const render = () => {
    const v = current();
    const t = v ? total(v) : 0;
    btn.disabled = !v || !v.a || st.busy;
    btn.textContent = !v ? 'Unavailable' : !v.a ? 'Sold out' : st.added ? box.dataset.added : label.replace('[total]', money(t));
    btn.classList.toggle('is-loading', st.busy);
    qtyEl.textContent = st.qty;
    setText($$('[data-buy-total]', root), money(t));
    setText($$('[data-buy-monthly]', root), money(Math.round(t / months)));
    $$('input[data-extra]', root).forEach((el) => el.closest('.extra')?.classList.toggle('is-on', el.checked));
    if (bar) {
      const colour = root.querySelector('input[data-short]:checked');
      const opts = $$('.vpick__card input:checked', root).map((el) => el.value);
      const sub = [colour && colour.dataset.short].concat(opts).filter(Boolean);
      if (st.qty > 1) sub.push('Qty ' + st.qty);
      setText($$('[data-sum-total]', bar), money(t));
      setText($$('[data-sum-subline]', bar), sub.join(' · '));
      setText($$('[data-add-to-cart]', bar), st.added ? 'Added ✓' : 'Add to cart');
      $$('[data-add-to-cart]', bar).forEach((b) => { b.classList.toggle('is-loading', st.busy); b.disabled = st.busy; });
    }
  };

  const selectVariant = () => {
    const v = current();
    if (!v) return;
    btn.dataset.variantId = v.id;
    const url = new URL(location.href);
    url.searchParams.set('variant', v.id);
    history.replaceState(history.state, '', url);
    document.dispatchEvent(new CustomEvent('s4s:variant-change', { detail: { variantId: v.id, variant: v } }));
  };

  const add = async () => {
    const v = current();
    if (!v || !v.a || st.busy) return;
    st.busy = true; render();
    const tile = root.querySelector('input[data-ship]:checked');
    // Lead time: variant (s4s.lead_time) → colour tile → product default / block setting
    const lead = v.l || (tile && tile.dataset.ship) || box.dataset.lead;
    const items = [{ id: v.id, quantity: st.qty, properties: lead ? { 'Lead time': lead } : {} }].concat(extras().map((el) => ({
      id: Number(el.value), quantity: st.qty, parent_id: v.id, properties: { _group: 'addon', _mode: 'qty' },
    })));
    try {
      // Nested lines need a parent in the same request; retry flat if the store rejects parent_id
      try { await S4S.cart.add(items); } catch (e1) {
        if (items.length < 2) throw e1;
        await S4S.cart.add(items.map(({ parent_id, ...i }) => i)); // eslint-disable-line no-unused-vars
      }
      flashAdded(st, render);
      S4S.cart.open();
    } finally { st.busy = false; render(); }
  };

  root.addEventListener('change', (e) => {
    if (e.target.matches('input[data-option-index]')) selectVariant();
    else if (!e.target.matches('input[data-extra]')) return;
    st.added = false; render();
  });
  box.addEventListener('click', (e) => {
    if (e.target.closest('[data-buy-inc]')) st.qty = Math.min(max, st.qty + 1);
    else if (e.target.closest('[data-buy-dec]')) st.qty = Math.max(1, st.qty - 1);
    else if (e.target.closest('[data-buy-add]')) return add();
    else return;
    st.added = false; render();
  });

  // Mobile sticky bar: shows once the buy row is off screen (and scrollY > 400); "Review" scrolls back to it
  // until the row has been passed, then "Add to cart".
  if (bar) {
    const row = box.querySelector('.buy__row');
    const mq = matchMedia('(max-width: 989px)');
    bar.addEventListener('click', (e) => {
      if (e.target.closest('[data-add-to-cart]')) add();
      else if (e.target.closest('[data-scroll-summary]')) {
        const el = root.querySelector('#financing') || row;
        scrollTo({ top: el.getBoundingClientRect().top + scrollY - 80, behavior: 'smooth' });
      }
    });
    const update = () => {
      const r = row.getBoundingClientRect();
      const on = mq.matches && !(r.top < innerHeight && r.bottom > 0) && scrollY > 400;
      bar.hidden = !on;
      bar.classList.toggle('is-ready', r.top < 0);
      document.body.classList.toggle('has-sticky-atc', on);
    };
    let raf = 0;
    addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; update(); }); }, { passive: true });
    mq.addEventListener('change', update);
    update();
  }

  render();
}

if (root && !cfgEl) initBuyButtons(root);
if (cfgEl) initConfigurator(root, JSON.parse(cfgEl.textContent));
